import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Eye } from "lucide-react";
import { formatPrice, productUrl } from "@/lib/storefront";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import QuickView from "@/components/storefront/QuickView";

const NEW_DAYS = 30;

function isNew(product) {
  if (!product?.created_at) return false;
  try {
    const ageDays = (Date.now() - new Date(product.created_at).getTime()) / 86400000;
    return ageDays >= 0 && ageDays <= NEW_DAYS;
  } catch {
    return false;
  }
}

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const [qvOpen, setQvOpen] = useState(false);

  const isWishlisted = has(product.id);
  const hasSale = product.old_price && product.old_price > product.price;
  const soldOut = (product.stock || 0) <= 0 && !product.allow_backorder;
  const fresh = isNew(product);
  const featured = !!product.featured || !!product.is_bestseller;
  const savePct = hasSale ? Math.round((1 - product.price / product.old_price) * 100) : 0;

  const primary = product.primary_image;

  return (
    <>
      <div
        className={`store-card group relative store-fade-up ${index < 4 ? `store-fade-up-d${index}` : ""}`}
        data-testid={`product-card-${product.id}`}
      >
        <Link to={productUrl(product)} className="block">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#E8E2D2]">
            {primary ? (
              <img
                src={primary}
                alt={product.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[#2D2823]/40">
                <div className="font-display text-2xl">No image</div>
              </div>
            )}

            {/* Badges */}
            <div className="absolute left-3 top-3 flex flex-col gap-1.5" data-testid={`badges-${product.id}`}>
              {hasSale && (
                <span className="rounded-full bg-[#1A1A1A] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-white" data-testid={`badge-sale-${product.id}`}>
                  -{savePct}%
                </span>
              )}
              {fresh && !hasSale && (
                <span className="rounded-full bg-[#7D8471] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-white" data-testid={`badge-new-${product.id}`}>
                  New
                </span>
              )}
              {featured && !fresh && !hasSale && (
                <span className="rounded-full bg-[#C2B280] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#1A1A1A]" data-testid={`badge-bestseller-${product.id}`}>
                  Best&nbsp;seller
                </span>
              )}
              {soldOut && (
                <span className="rounded-full bg-white/95 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#1A1A1A]" data-testid={`badge-soldout-${product.id}`}>
                  Sold out
                </span>
              )}
            </div>

            {/* Wishlist (top-right) */}
            <button
              onClick={(e) => { e.preventDefault(); toggle(product); }}
              aria-label="Toggle wishlist"
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#1A1A1A] opacity-0 transition-opacity hover:bg-white group-hover:opacity-100 md:opacity-0"
              data-testid={`wishlist-toggle-${product.id}`}
            >
              <Heart size={16} className={isWishlisted ? "fill-[#7D8471] text-[#7D8471]" : ""} />
            </button>

            {/* Quick view (top-right under heart) */}
            <button
              onClick={(e) => { e.preventDefault(); setQvOpen(true); }}
              aria-label="Quick view"
              className="absolute right-3 top-14 hidden h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#1A1A1A] opacity-0 transition-opacity hover:bg-white group-hover:opacity-100 md:flex"
              data-testid={`quick-view-${product.id}`}
            >
              <Eye size={16} />
            </button>

            {/* Add to bag — desktop only on hover, mobile always visible at the bottom */}
            <button
              onClick={(e) => { e.preventDefault(); addItem(product, 1); }}
              disabled={soldOut}
              className="absolute bottom-3 left-3 right-3 translate-y-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm text-white opacity-0 transition-all hover:bg-[#7D8471] disabled:pointer-events-none disabled:bg-[#2D2823]/50 group-hover:translate-y-0 group-hover:opacity-100 md:opacity-0"
              data-testid={`add-to-cart-${product.id}`}
            >
              {soldOut ? "Sold out" : (
                <span className="inline-flex items-center justify-center gap-2">
                  <ShoppingBag size={14} /> Add to bag
                </span>
              )}
            </button>
          </div>

          <div className="pt-4">
            {product.brand_id && product.brand?.name && (
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#2D2823]">{product.brand.name}</div>
            )}
            <div className="mt-1 line-clamp-2 text-[15px] font-medium leading-snug text-[#1A1A1A] sm:text-[16px]" data-testid={`product-title-${product.id}`}>
              {product.title}
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 store-nums">
              <span className="text-[15px] font-semibold text-[#1A1A1A] sm:text-base">{formatPrice(product.price, product.currency)}</span>
              {hasSale && (
                <span className="text-[13px] text-[#2D2823] line-through sm:text-sm">{formatPrice(product.old_price, product.currency)}</span>
              )}
            </div>
          </div>
        </Link>
      </div>

      <QuickView open={qvOpen} productId={product.id} onClose={() => setQvOpen(false)} />
    </>
  );
}
